/**
 * The oil / service status of every car - ONE rule for the Fleet page's "Oil Due" card and oil column
 * and Service History's "Upcoming / Due" list and badge (audit FLT-14: the two pages counted with two
 * copies of the rule over two different car lists).
 *
 * The override first, else the last service's next KM (or its KM + 10000; 10000 with no service) and
 * next date; DUE at or past either, SOON within 500 km or 10 days. Computed once per data change
 * (plate -> result), not cars x records per render (audit FLT-21).
 */
import { normalizePlate as sharedNormalizePlate } from './possessionMatcher';

const plateKey = (plate) => sharedNormalizePlate(plate).toLowerCase();

/**
 * plate -> { label, color, targetKm, targetDate, kmRemaining, daysRemaining }.
 * `kmOf(plate)` gives the car's current KM.
 */
export const makeOilPlan = ({ cars, serviceRecords, kmOf, today = new Date() }) => {
    const lastByPlate = new Map();
    (serviceRecords || []).forEach((r) => {
        const key = plateKey(r.vehicleId);
        const prev = lastByPlate.get(key);
        if (!prev || new Date(r.date) > new Date(prev.date)) lastByPlate.set(key, r);
    });
    const carByPlate = new Map();
    (cars || []).forEach((c) => {
        const key = plateKey(c.plate_number);
        if (!carByPlate.has(key)) carByPlate.set(key, c);
    });
    const cache = new Map();

    return (plate) => {
        if (!plate) return { label: 'No Data', color: 'gray', targetKm: null, targetDate: null, kmRemaining: null, daysRemaining: null };
        const key = plateKey(plate);
        if (cache.has(key)) return cache.get(key);

        const lastService = lastByPlate.get(key);
        const vehicle = carByPlate.get(key);
        const currentKm = Number(kmOf(plate)) || 0;
        const targetKm = vehicle?.nextServiceKmOverride
            ? Number(vehicle.nextServiceKmOverride)
            : (lastService ? (Number(lastService.nextServiceKm) || (Number(lastService.currentKm) + 10000)) : 10000);
        const targetDate = vehicle?.nextServiceDateOverride
            ? new Date(vehicle.nextServiceDateOverride)
            : (lastService?.nextServiceDate ? new Date(lastService.nextServiceDate) : null);

        const kmRemaining = targetKm - currentKm;
        let dateDue = false;
        let dateSoon = false;
        let daysRemaining = null;
        if (targetDate) {
            daysRemaining = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
            dateDue = daysRemaining <= 0;
            dateSoon = daysRemaining <= 10;
        }

        let status = { label: 'OK', color: 'green' };
        if (kmRemaining <= 0 || dateDue) status = { label: 'DUE', color: 'red' };
        else if (kmRemaining <= 500 || dateSoon) status = { label: 'SOON', color: 'yellow' };
        const plan = { ...status, currentKm, targetKm, targetDate, kmRemaining, daysRemaining };
        cache.set(key, plan);
        return plan;
    };
};

/** plate -> { label, color } (the Fleet page's oil column and card). */
export const makeOilStatus = (args) => {
    const plan = makeOilPlan(args);
    return (plate) => {
        const { label, color } = plan(plate);
        return { label, color };
    };
};
